import {
  IQuoteRequestSchema,
  IQuoteRespSchema,
  RuleValidationErrorLevelEnum,
} from '@monetize/types/app';
import { endOfDay } from 'date-fns/endOfDay';
import { isBefore } from 'date-fns/isBefore';
import { parseISO } from 'date-fns/parseISO';
import { FC, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useGetQuoteStorageById } from '../../../../../api/cpqService';
import { MButton, MTooltip } from '../../../../../components/Monetize';
import { MTooltipContentList } from '../../../../../components/Monetize/MTooltipContentList';
import { useQuoteDocumentUploadsSummary } from '../../../../../hooks/useQuoteDocumentUploads';
import { pluralize } from '../../../../../utils';
import { getEmptyQuoteOfferingGroups } from '../../../../../utils/quotes';
import { QuoteModalDataTypes } from '../../../Quote/QuoteReviewModal';
import {
  getEsignContactValidity,
  getStandardContactsValidity,
} from '../../../Quote/components/contacts/quoteContactUtils';
import {
  QuoteStateDataTypes,
  useQuoteContext,
} from '../../../Quote/quoteContext';

interface QuoteDraftSubmitButtonProps {
  isReadOnly: boolean;
  isActionLoading: boolean;
  quote: IQuoteRespSchema;
  quoteStateData: QuoteStateDataTypes;
  quoteFormMethods: UseFormReturn<IQuoteRequestSchema>;
  reviewQuoteModalData: QuoteModalDataTypes;
}

export const QuoteDraftSubmitButton: FC<QuoteDraftSubmitButtonProps> = ({
  isReadOnly,
  isActionLoading,
  quote,
  quoteStateData,
  quoteFormMethods,
  reviewQuoteModalData,
}: QuoteDraftSubmitButtonProps) => {
  const { isReviewBtnPressed } = quoteStateData;
  const { quoteOfferingGroups } = useQuoteContext();
  const {
    formState: { errors },
  } = quoteFormMethods;

  const emptyQuoteOfferingGroups = useMemo(
    () =>
      getEmptyQuoteOfferingGroups(
        quoteOfferingGroups?.groups,
        quote.quoteOfferings,
      ),
    [quoteOfferingGroups?.groups, quote.quoteOfferings],
  );

  const expirationDateIsInPast = useMemo(
    () => isBefore(endOfDay(parseISO(quote.expirationDate)), new Date()),
    [quote.expirationDate],
  );

  const sowDocument = useGetQuoteStorageById(
    {
      quoteId: quote.id,
      storageId: quote.sowDocumentStorageId!,
      params: { attachContent: true },
    },
    { enabled: !!quote.sowDocumentStorageId },
  );

  const msaDocument = useGetQuoteStorageById(
    {
      quoteId: quote.id,
      storageId: quote.documentLocation!,
      params: { attachContent: true },
    },
    { enabled: !!quote.documentLocation },
  );

  const isDocumentsLoading = msaDocument.isLoading || sowDocument.isLoading;

  const { uploadingCount } = useQuoteDocumentUploadsSummary(quote.id);

  const {
    isValid: isStandardContactsValid,
    errorMsgs: standardContactsErrorMsgs,
  } = getStandardContactsValidity(quote);

  const { isValid: isEsignSetupValid, errorMsgs: eSignContactsErrorMsgs } =
    getEsignContactValidity(quote);

  const errorValidations =
    quote?.validations?.filter(
      (validation) =>
        validation.errorLevel === RuleValidationErrorLevelEnum.ERROR,
    ) || [];

  const isDisabled =
    !(
      isStandardContactsValid &&
      quote?.quoteOfferings?.length &&
      isEsignSetupValid
    ) ||
    expirationDateIsInPast ||
    isDocumentsLoading ||
    uploadingCount > 0 ||
    Object.keys(errors).length > 0 ||
    errorValidations.length > 0 ||
    emptyQuoteOfferingGroups.length > 0;

  let disabledSubmitMessages: string[] = [];

  if (isDisabled) {
    /** Common Error messages for All the quote status */
    if (!isStandardContactsValid) {
      disabledSubmitMessages = disabledSubmitMessages.concat(
        standardContactsErrorMsgs,
      );
    }

    if (!isEsignSetupValid) {
      disabledSubmitMessages = disabledSubmitMessages.concat(
        eSignContactsErrorMsgs,
      );
    }

    /** Error messages if quote in draft status */
    if (errorValidations.length > 0) {
      disabledSubmitMessages.push('Resolve validation error');
    }

    if (!quote.quoteOfferings?.length) {
      disabledSubmitMessages.push('At least one offering is required');
    }

    if (expirationDateIsInPast) {
      disabledSubmitMessages.push('Expiration date must be in the future');
    }

    if (isDocumentsLoading) {
      disabledSubmitMessages.push('Wait for documents to finish uploading');
    }

    if (uploadingCount > 0) {
      disabledSubmitMessages.push(
        `${uploadingCount} ${pluralize('document', uploadingCount)} ${
          uploadingCount === 1 ? 'is' : 'are'
        } being uploaded`,
      );
    }

    if (emptyQuoteOfferingGroups.length > 2) {
      disabledSubmitMessages.push(
        'Move offerings into each empty group or delete them',
      );
    } else if (emptyQuoteOfferingGroups.length > 0) {
      const allGroups = quoteOfferingGroups?.groups || [];
      disabledSubmitMessages = disabledSubmitMessages.concat(
        emptyQuoteOfferingGroups.map((group) => {
          const groupIndex = allGroups.findIndex(({ id }) => id === group.id);
          return `Move offerings into empty "Group ${groupIndex + 1}" or delete it`;
        }),
      );
    }
  }

  const sendTooltipContent = (
    <MTooltipContentList
      title="To submit, resolve required actions below"
      listItems={disabledSubmitMessages}
    />
  );

  return (
    <MTooltip
      shouldWrapChildren
      isDisabled={!isDisabled}
      bg="tPurple.base"
      label={sendTooltipContent}
    >
      <MButton
        mr={1.5}
        type="button"
        variant="primary"
        isLoading={isReviewBtnPressed}
        isDisabled={isDisabled || isReviewBtnPressed || isReadOnly}
        onClick={reviewQuoteModalData.handleReviewOrOpenModal}
      >
        Submit
      </MButton>
    </MTooltip>
  );
};
